import {onServerPrefetch} from "vue";
import {
	useQuery as origUseQuery,
	type AnyVariables,
	type UseQueryArgs,
	type UseQueryResponse,
} from "@urql/vue";

/**
 * A `useQuery` that is safe to render during SSR without having to `await` it, just like `useAsyncData`.
 *
 * A plain un-awaited `useQuery` doesn't make the server render wait for its result (like `useAsyncData`), so
 * the server may render the component without data. The result can still end up in the
 * SSR payload though if it resolves before the whole app finishes rendering, and the
 * client then hydrates with the data already present -> hydration mismatch.
 *
 * This registers the query with `onServerPrefetch`, like Nuxt's `useAsyncData` does, so
 * the server renderer waits for the result before rendering the component. Unlike
 * `await useQuery(...)`, this doesn't block client-side navigations through Suspense.
 *
 * Must be called synchronously inside a component's setup function.
 */
export function useQuery<T = unknown, V extends AnyVariables = AnyVariables>(
	args: UseQueryArgs<T, V>,
): UseQueryResponse<T, V> {
	const result = origUseQuery<T, V>(args);

	if (import.meta.server) {
		onServerPrefetch(async () => {
			await result;
		});
	}

	return result;
}
